import { useState } from 'react'
import {
  Bot,
  ChevronDown,
  Copy,
  Link2,
  Phone,
  Power,
  QrCode,
  RefreshCw,
  Settings2,
  Smartphone,
  Trash2,
} from 'lucide-react'

import { Button } from '@/components/common/Button'
import { useAgentCatalogStore } from '@/stores/agentCatalogStore'
import { cn } from '@/utils/cn'
import { INSTANCE_STATUS } from '@/utils/app-constants'
import { formatRelativeTime, formatWhatsappDisplayNumber } from '@/utils/formatters'
import type { Instance } from '@/types'

interface InstanceCardProps {
  instance: Instance
  onSettings?: () => void
  onDisconnect?: () => void
  onDelete?: () => void
  onViewQR?: () => void
  onTogglePublicLink?: (enabled: boolean) => void
  onCopyPublicLink?: () => void
  onBindAgent?: (agentId: string | null) => void
  isAdmin?: boolean
}

const statusStyles: Record<Instance['status'], string> = {
  connected: 'bg-[rgba(16,185,129,0.14)] text-[var(--color-success)] border-[rgba(16,185,129,0.2)]',
  connecting: 'bg-[rgba(245,158,11,0.14)] text-[var(--color-warning)] border-[rgba(245,158,11,0.2)]',
  qr_ready: 'bg-[rgba(59,130,246,0.14)] text-[var(--color-info)] border-[rgba(59,130,246,0.2)]',
  disconnected: 'bg-[rgba(239,68,68,0.14)] text-[var(--color-error)] border-[rgba(239,68,68,0.2)]',
}

export function InstanceCard({
  instance,
  onSettings,
  onDisconnect,
  onDelete,
  onViewQR,
  onTogglePublicLink,
  onCopyPublicLink,
  onBindAgent,
  isAdmin = false,
}: InstanceCardProps) {
  const [agentPickerOpen, setAgentPickerOpen] = useState(false)
  const statusInfo = INSTANCE_STATUS[instance.status]
  const agents = useAgentCatalogStore((state) => state.agents)
  const boundAgent = agents.find((agent) => {
    const binding = instance.settings?.ai_agent_binding as { agent_id?: string } | undefined
    return agent.id === binding?.agent_id
  })
  const boundAgentId = (
    instance.settings?.ai_agent_binding as { agent_id?: string } | undefined
  )?.agent_id
  const activeSubagents = boundAgent?.subagents?.filter((subagent) => subagent.active).length ?? 0
  const fallbackRuntimeProfile = (instance.settings?.agent_runtime_profile as {
    persona_name?: string
    supervisor_name?: string
    enabled?: boolean
    subagents?: Array<{ active?: boolean }>
  } | undefined)
  const fallbackSubagents =
    fallbackRuntimeProfile?.subagents?.filter((subagent) => subagent.active !== false).length ?? 0
  const primaryActionLabel =
    instance.status === 'connected'
      ? 'Detalhes'
      : instance.status === 'disconnected'
        ? 'Reconectar'
        : 'Abrir QR'

  return (
    <article className="group relative flex h-full min-h-[390px] flex-col overflow-hidden rounded-[28px] border border-[rgba(255,255,255,0.05)] bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))] p-6 shadow-[0_24px_60px_rgba(7,9,30,0.28)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_30px_80px_rgba(7,9,30,0.36)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(var(--color-brand-rgb),0.12),transparent_45%)] opacity-0 transition duration-300 group-hover:opacity-100" />

      <div className="relative flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[22px] border border-[rgba(var(--color-brand-rgb),0.24)] bg-[radial-gradient(circle_at_top,rgba(var(--color-brand-rgb),0.32),rgba(var(--color-brand-rgb),0.08))] text-white shadow-[0_0_24px_rgba(var(--color-brand-rgb),0.16)]">
            {instance.profile_picture_url ? (
              <img
                src={instance.profile_picture_url}
                alt={`Foto de perfil da instância ${instance.name}`}
                className="h-full w-full object-cover"
              />
            ) : (
              <Smartphone size={26} />
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h3 className="text-2xl font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
                {instance.name}
              </h3>
              <span className={cn('status-dot', `status-dot-${instance.status}`)} />
            </div>

            <div
              className={cn(
                'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium',
                statusStyles[instance.status],
              )}
            >
              <span className="h-2 w-2 rounded-full bg-current" />
              {statusInfo.label}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSettings}
            className="inline-flex h-11 w-11 items-center justify-center rounded-[14px] border border-[var(--border-subtle)] bg-[var(--glass-light)] text-[var(--text-secondary)] transition hover:border-[var(--border-medium)] hover:text-[var(--text-primary)]"
            aria-label="Configurações da instância"
          >
            <Settings2 size={18} />
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex h-11 w-11 items-center justify-center rounded-[14px] border border-[rgba(239,68,68,0.16)] bg-[rgba(239,68,68,0.08)] text-[var(--color-error)] transition hover:bg-[rgba(239,68,68,0.12)]"
              aria-label="Excluir instância"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="relative mt-6 grid gap-3">
        <div className="flex items-center justify-between rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm">
          <span className="inline-flex items-center gap-2 text-[var(--text-muted)]">
            <Link2 size={14} />
            ID da conexão
          </span>
          <span className="font-medium text-[var(--text-primary)]">
            {instance.remote_instance_id ?? instance.id.slice(0, 8)}
          </span>
        </div>

        <div className="flex items-center justify-between rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-sm">
          <span className="inline-flex items-center gap-2 text-[var(--text-muted)]">
            <Phone size={14} />
            Número vinculado
          </span>
          <span className="font-medium text-[var(--text-primary)]">
            {formatWhatsappDisplayNumber(instance.phone_number) || 'Aguardando conexão'}
          </span>
        </div>
      </div>

      <div className="relative mt-5 rounded-[22px] border border-[var(--border-subtle)] bg-[rgba(12,14,40,0.26)] p-4">
        <div className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
          <Bot size={16} className="text-[var(--color-brand)]" />
          Follow-up
        </div>

        {agents.length > 0 ? (
          <div className="space-y-3">
            <div className="rounded-[16px] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)]">
              <button
                type="button"
                onClick={() => setAgentPickerOpen((current) => !current)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
              >
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
                    Fluxo vinculado
                  </p>
                  <p className="mt-1 text-sm font-medium text-[var(--text-primary)]">
                    {boundAgent?.name ?? 'Nenhum fluxo vinculado'}
                  </p>
                </div>
                <ChevronDown
                  size={16}
                  className={cn(
                    'text-[var(--text-muted)] transition',
                    agentPickerOpen ? 'rotate-180' : '',
                  )}
                />
              </button>

              {agentPickerOpen ? (
                <div className="border-t border-[var(--border-subtle)] px-3 py-3">
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    <button
                      type="button"
                      onClick={() => {
                        onBindAgent?.(null)
                        setAgentPickerOpen(false)
                      }}
                      className={cn(
                        'w-full rounded-[14px] border px-3 py-3 text-left text-sm transition',
                        !boundAgentId
                          ? 'border-[rgba(var(--color-brand-rgb),0.32)] bg-[rgba(var(--color-brand-rgb),0.18)] text-[var(--text-primary)]'
                          : 'border-[var(--border-subtle)] bg-[var(--glass-light)] text-[var(--text-secondary)] hover:border-[var(--border-medium)] hover:text-[var(--text-primary)]',
                      )}
                    >
                      Nenhum fluxo vinculado
                    </button>

                    {agents.map((agent) => {
                      const active = agent.id === boundAgentId
                      return (
                        <button
                          key={agent.id}
                          type="button"
                          onClick={() => {
                            onBindAgent?.(agent.id)
                            setAgentPickerOpen(false)
                          }}
                          className={cn(
                            'w-full rounded-[14px] border px-3 py-3 text-left transition',
                            active
                              ? 'border-[rgba(var(--color-brand-rgb),0.32)] bg-[rgba(var(--color-brand-rgb),0.18)] text-[var(--text-primary)]'
                              : 'border-[var(--border-subtle)] bg-[var(--glass-light)] text-[var(--text-secondary)] hover:border-[var(--border-medium)] hover:text-[var(--text-primary)]',
                          )}
                        >
                          <p className="text-sm font-medium">{agent.name}</p>
                          <p className="mt-1 text-xs text-[var(--text-muted)]">
                            {agent.persona_name || agent.supervisor_name}
                          </p>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-[16px] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] px-4 py-3 text-sm text-[var(--text-secondary)]">
              {boundAgent ? (
                <div className="space-y-1">
                  <p className="font-medium text-[var(--text-primary)]">
                    {boundAgent.persona_name || boundAgent.supervisor_name || boundAgent.name}
                  </p>
                  <p>
                    {boundAgent.enabled ? 'Ativo' : 'Pausado'} · {activeSubagents} subagente(s)
                  </p>
                </div>
              ) : fallbackRuntimeProfile ? (
                <div className="space-y-1">
                  <p className="font-medium text-[var(--text-primary)]">
                    {fallbackRuntimeProfile.persona_name || fallbackRuntimeProfile.supervisor_name || 'Supervisor configurado'}
                  </p>
                  <p>
                    {fallbackRuntimeProfile.enabled ? 'Ativo' : 'Pausado'} · {fallbackSubagents} subagente(s)
                  </p>
                </div>
              ) : (
                'Selecione um fluxo em Monitoramento > Follow-up.'
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-[16px] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] px-4 py-3 text-sm text-[var(--text-secondary)]">
            {fallbackRuntimeProfile ? (
            <div className="space-y-1">
              <p className="font-medium text-[var(--text-primary)]">
                {fallbackRuntimeProfile.persona_name || fallbackRuntimeProfile.supervisor_name || 'Supervisor configurado'}
              </p>
              <p>
                {fallbackRuntimeProfile.enabled ? 'Ativo' : 'Pausado'} · {fallbackSubagents} subagente(s)
              </p>
            </div>
          ) : (
            'Crie um fluxo primeiro em Monitoramento > Follow-up.'
          )}
          </div>
        )}
      </div>

      <div className="relative mt-6 grid gap-3 sm:grid-cols-2">
        <Button
          variant={instance.status === 'disconnected' ? 'success' : 'outline'}
          className="h-12 justify-center rounded-[16px]"
          icon={instance.status === 'disconnected' ? <RefreshCw size={16} /> : <QrCode size={16} />}
          onClick={onViewQR}
        >
          {primaryActionLabel}
        </Button>

        <Button
          variant={instance.status === 'connected' ? 'danger' : 'brand'}
          className="h-12 justify-center rounded-[16px]"
          icon={instance.status === 'connected' ? <Power size={16} /> : <RefreshCw size={16} />}
          onClick={instance.status === 'connected' ? onDisconnect : onViewQR}
        >
          {instance.status === 'connected' ? 'Desconectar' : 'Gerar QR'}
        </Button>
      </div>

      <div className="relative mt-auto pt-6">
        <div className="flex items-center justify-between rounded-[20px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3">
          <div>
            <p className="inline-flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
              <Link2 size={15} />
              Link Público QR
            </p>
            <p className="mt-1 text-xs text-[var(--text-muted)]">
              Atualizado {formatRelativeTime(instance.updated_at)}
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={instance.public_link_enabled}
            onClick={() => onTogglePublicLink?.(!instance.public_link_enabled)}
            className={cn(
              'relative inline-flex h-8 w-14 items-center rounded-full border transition',
              instance.public_link_enabled
                ? 'border-[rgba(var(--color-brand-rgb),0.28)] bg-[rgba(var(--color-brand-rgb),0.28)]'
                : 'border-[var(--border-subtle)] bg-[rgba(255,255,255,0.08)]',
            )}
          >
            <span
              className={cn(
                'inline-block h-6 w-6 rounded-full bg-white shadow transition',
                instance.public_link_enabled ? 'translate-x-7' : 'translate-x-1',
              )}
            />
          </button>
        </div>

        <Button
          variant="ghost"
          fullWidth
          className="mt-3 h-11 justify-center rounded-[16px] border border-[var(--border-subtle)] bg-[rgba(48,69,120,0.18)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          icon={<Copy size={14} />}
          onClick={onCopyPublicLink}
          disabled={!instance.public_link_enabled || !instance.public_qr_url}
        >
          Copiar Link para Cliente
        </Button>
      </div>
    </article>
  )
}
